import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { supabase } from '../services/supabase';
import { thumbUrl } from '../utils/imageUrl';
import { compressImage } from '../utils/compressImage';
import { FaPlus, FaEdit, FaTrash, FaEye, FaStar } from 'react-icons/fa';
import '../styles/CampaignsPage.css';

const CampaignsPage = () => {
  const { business } = useAuth();
  const { locations, locationId } = useLocation();
  const [campaigns, setCampaigns] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterLocId, setFilterLocId] = useState('all'); // 'all' or location id

  const [bannerUploading, setBannerUploading] = useState(false);

  // Which locations to create campaign for (multi-select in form)
  const [selectedLocIds, setSelectedLocIds] = useState([]);

  const isChainOwner = locations.length > 1;

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'general',
    campaign_type: 'discount',
    discount_percentage: '',
    points_required: '',
    free_item_name: '',
    original_price: '',
    discounted_price: '',
    badge_text: '',
    badge_color: '#e74c3c',
    duration_type: 'daily',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    is_featured: false,
    priority: 0,
    banner_url: '',
    buy_item_name: '',
    get_item_name: '',
    get_item_discount: '100',
    is_corporate_only: false,
  });

  useEffect(() => { loadCampaigns(); }, [business, filterLocId]); // eslint-disable-line

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      let q = supabase
        .from('campaigns')
        .select('*')
        .eq('business_id', business.id)
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false });
      if (filterLocId !== 'all') q = q.eq('location_id', filterLocId);
      const { data, error } = await q;
      if (error) throw error;
      setCampaigns(data || []);
    } catch (error) {
      console.error('Load campaigns error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isChainOwner && !editingCampaign && selectedLocIds.length === 0) {
      alert('❌ Ən az 1 filial seçin');
      return;
    }
    setLoading(true);

    try {
      // Calculate end_date based on duration_type
      // For non-custom types, always recalculate from start_date so stale dates don't persist
      let endDate = formData.end_date;
      if (formData.duration_type !== 'custom' || !endDate) {
        const start = new Date(formData.start_date);
        switch (formData.duration_type) {
          case 'daily':
            start.setDate(start.getDate() + 1);
            break;
          case 'weekly':
            start.setDate(start.getDate() + 7);
            break;
          case 'monthly':
            start.setMonth(start.getMonth() + 1);
            break;
          default:
            start.setDate(start.getDate() + 1);
        }
        endDate = start.toISOString();
      }

      const base = {
        ...formData,
        business_id: business.id,
        end_date: endDate,
        discount_percentage: formData.discount_percentage ? parseInt(formData.discount_percentage) : null,
        points_required: formData.points_required ? parseInt(formData.points_required) : null,
        original_price: formData.original_price ? parseFloat(formData.original_price) : null,
        discounted_price: formData.discounted_price ? parseFloat(formData.discounted_price) : null,
        priority: parseInt(formData.priority),
        buy_item_name: formData.buy_item_name || null,
        get_item_name: formData.get_item_name || null,
        get_item_discount: formData.get_item_discount ? parseInt(formData.get_item_discount) : null,
      };

      if (editingCampaign) {
        const { error } = await supabase
          .from('campaigns')
          .update({ ...base, is_active: true })
          .eq('id', editingCampaign.id);
        if (error) throw error;
        alert('✅ Kampanya yeniləndi!');
      } else {
        // Create one record per selected branch (or single if not chain)
        const targetIds = isChainOwner && selectedLocIds.length > 0
          ? selectedLocIds
          : [locationId || null];

        const rows = targetIds.map(lid => ({ ...base, location_id: lid }));
        const { error } = await supabase.from('campaigns').insert(rows);
        if (error) throw error;
        alert(`✅ Kampanya ${rows.length > 1 ? rows.length + ' filiala' : ''} yaradıldı!`);
      }

      setShowModal(false);
      resetForm();
      loadCampaigns();
    } catch (error) {
      console.error('Save campaign error:', error);
      alert('❌ Xəta baş verdi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBannerUploading(true);
    try {
      const compressed = await compressImage(file, { maxWidth: 1200, quality: 0.82 });
      const path = `campaigns/${business.id}/${Date.now()}.jpg`;
      const { error } = await supabase.storage
        .from('business-photos')
        .upload(path, compressed, { upsert: true, contentType: 'image/jpeg' });
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage
        .from('business-photos')
        .getPublicUrl(path);
      setFormData(prev => ({ ...prev, banner_url: publicUrl }));
    } catch (err) {
      alert('Şəkil yüklənmədi: ' + err.message);
    } finally {
      setBannerUploading(false);
    }
  };

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      title: campaign.title,
      description: campaign.description || '',
      campaign_type: campaign.campaign_type,
      discount_percentage: campaign.discount_percentage || '',
      points_required: campaign.points_required || '',
      free_item_name: campaign.free_item_name || '',
      original_price: campaign.original_price || '',
      discounted_price: campaign.discounted_price || '',
      buy_item_name: campaign.buy_item_name || '',
      get_item_name: campaign.get_item_name || '',
      get_item_discount: campaign.get_item_discount != null ? String(campaign.get_item_discount) : '100',
      badge_text: campaign.badge_text || '',
      badge_color: campaign.badge_color || '#e74c3c',
      duration_type: campaign.duration_type,
      start_date: campaign.start_date.split('T')[0],
      // For non-custom types, clear end_date so it's recalculated from start_date on save
      end_date: campaign.duration_type === 'custom' && campaign.end_date
        ? campaign.end_date.split('T')[0]
        : '',
      is_featured: campaign.is_featured,
      priority: campaign.priority,
      banner_url: campaign.banner_url || '',
      category: campaign.category || 'general',
      is_corporate_only: campaign.is_corporate_only || false,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Kampanyanı silmək istədiyinizə əminsiniz?')) return;

    try {
      const { error } = await supabase
        .from('campaigns')
        .delete()
        .eq('id', id);

      if (error) throw error;
      alert('✅ Kampanya silindi!');
      loadCampaigns();
    } catch (error) {
      console.error('Delete campaign error:', error);
      alert('❌ Xəta baş verdi');
    }
  };

  const toggleActive = async (campaign) => {
    try {
      const { error } = await supabase
        .from('campaigns')
        .update({ is_active: !campaign.is_active })
        .eq('id', campaign.id);

      if (error) throw error;
      loadCampaigns();
    } catch (error) {
      console.error('Toggle active error:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      campaign_type: 'discount',
      discount_percentage: '',
      points_required: '',
      free_item_name: '',
      original_price: '',
      discounted_price: '',
      badge_text: '',
      badge_color: '#e74c3c',
      duration_type: 'daily',
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
      is_featured: false,
      priority: 0,
      buy_item_name: '',
      get_item_name: '',
      get_item_discount: '100',
      banner_url: '',
      is_corporate_only: false,
    });
    setEditingCampaign(null);
    setSelectedLocIds([]);
  };

  const getCampaignTypeLabel = (type) => {
    const labels = {
      discount: '💰 Endirim',
      points_boost: '⭐ Xal Bonus',
      free_item: '🎁 Pulsuz Məhsul',
      combo_deal: '🍔 Kombo Təklif',
      bogo: '🎁 1 Alana 1 Hədiyyə',
    };
    return labels[type] || type;
  };

  const getDurationLabel = (type) => {
    const labels = {
      daily: 'Günün Kampaniyası',
      weekly: 'Həftənin Kampaniyası',
      monthly: 'Ayın Kampaniyası',
      custom: 'Xüsusi Müddət'
    };
    return labels[type] || type;
  };

  if (loading && campaigns.length === 0) {
    return <div className="campaigns-loading">Yüklənir...</div>;
  }

  return (
    <div className="campaigns-page">
      <div className="campaigns-header">
        <div>
          <h1>📢 Kampanya İdarəetməsi</h1>
          <p>Məhsul tanıtımları və endirimlər yaradın</p>
        </div>
        <button className="create-campaign-btn" onClick={() => setShowModal(true)}>
          <FaPlus /> Yeni Kampanya
        </button>
      </div>

      {/* Filial filter tabları — yalnız chain üçün */}
      {isChainOwner && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
          <button
            onClick={() => setFilterLocId('all')}
            style={tabStyle(filterLocId === 'all')}
          >
            🏪 Hamısı
          </button>
          {locations.map(loc => (
            <button
              key={loc.id}
              onClick={() => setFilterLocId(loc.id)}
              style={tabStyle(filterLocId === loc.id)}
            >
              📍 {loc.name}
            </button>
          ))}
        </div>
      )}

      {campaigns.length === 0 ? (
        <div className="empty-campaigns">
          <p>Hələ kampaniya yoxdur</p>
          <button onClick={() => setShowModal(true)}>İlk Kampanyanı Yarat</button>
        </div>
      ) : (
        <div className="campaigns-grid">
          {campaigns.map((campaign) => (
            <div key={campaign.id} className={`campaign-card ${!campaign.is_active ? 'inactive' : ''}`}>
              {campaign.banner_url && (
                <img
                  src={thumbUrl(campaign.banner_url)}
                  alt="banner"
                  style={{
                    width: '100%', height: 120, objectFit: 'cover',
                    borderRadius: 8, marginBottom: 15,
                  }}
                />
              )}
              {campaign.is_featured && (
                <div className="featured-badge">
                  <FaStar /> ÖNƏ ÇIXARILMIŞ
                </div>
              )}

              {isChainOwner && campaign.location_id && (() => {
                const loc = locations.find(l => l.id === campaign.location_id);
                return loc ? (
                  <div style={{ fontSize: 11, color: '#f97316', fontWeight: 700, marginBottom: 8 }}>
                    📍 {loc.name}
                  </div>
                ) : null;
              })()}

              <div className="campaign-header">
                <span
                  className="campaign-badge"
                  style={{ backgroundColor: campaign.badge_color }}
                >
                  {campaign.badge_text || getCampaignTypeLabel(campaign.campaign_type)}
                </span>
                <span className="duration-badge">{getDurationLabel(campaign.duration_type)}</span>
              </div>

              <h3>{campaign.title}</h3>
              {campaign.description && <p className="campaign-description">{campaign.description}</p>}

              <div className="campaign-details">
                {campaign.campaign_type === 'discount' && (
                  <div className="detail-item">
                    <strong>{campaign.discount_percentage}% Endirim</strong>
                    {campaign.original_price && (
                      <span className="price-info">
                        <span className="old-price">{campaign.original_price}₼</span>
                        <span className="new-price">{campaign.discounted_price}₼</span>
                      </span>
                    )}
                  </div>
                )}

                {campaign.campaign_type === 'points_boost' && (
                  <div className="detail-item">
                    <strong>{campaign.points_required} xal ilə al</strong>
                    <span className="boost-info">Əvvəlki: 100 xal</span>
                  </div>
                )}

                {campaign.campaign_type === 'free_item' && (
                  <div className="detail-item">
                    <strong>🎁 {campaign.free_item_name}</strong>
                    <span>Pulsuz!</span>
                  </div>
                )}

                {campaign.campaign_type === 'bogo' && (
                  <div className="detail-item bogo-detail">
                    <div className="bogo-row">
                      <span className="bogo-buy">🛒 Al: <strong>{campaign.buy_item_name}</strong></span>
                      <span className="bogo-arrow">→</span>
                      <span className="bogo-get">
                        🎁 Qazanırsın: <strong>{campaign.get_item_name}</strong>
                        {campaign.get_item_discount === 100
                          ? <span className="bogo-free-tag">Pulsuz</span>
                          : <span className="bogo-discount-tag">{campaign.get_item_discount}% endirim</span>
                        }
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="campaign-dates">
                <span>📅 {new Date(campaign.start_date).toLocaleDateString('az-AZ')}</span>
                <span>→</span>
                <span>{new Date(campaign.end_date).toLocaleDateString('az-AZ')}</span>
              </div>

              <div className="campaign-stats">
                <span><FaEye /> {campaign.view_count} baxış</span>
                <span>👆 {campaign.click_count} klik</span>
              </div>

              <div className="campaign-actions">
                <button
                  className={`toggle-btn ${campaign.is_active ? 'active' : 'inactive'}`}
                  onClick={() => toggleActive(campaign)}
                >
                  {campaign.is_active ? '✅ Aktiv' : '⏸️ Deaktiv'}
                </button>
                <button className="edit-btn" onClick={() => handleEdit(campaign)}>
                  <FaEdit />
                </button>
                <button className="delete-btn" onClick={() => handleDelete(campaign.id)}>
                  <FaTrash />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); resetForm(); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingCampaign ? 'Kampanyanı Düzəliş Et' : 'Yeni Kampanya Yarat'}</h2>

            <form onSubmit={handleSubmit}>
              {/* Filial seçimi — yalnız chain owner + yeni kampaniya */}
              {isChainOwner && !editingCampaign && (
                <div className="form-group" style={{ marginBottom: 18 }}>
                  <label>📍 Filiallar *</label>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                    <button
                      type="button"
                      onClick={() => setSelectedLocIds(locations.map(l => l.id))}
                      style={{ padding: '5px 14px', borderRadius: 8, border: '1.5px solid #f97316', background: selectedLocIds.length === locations.length ? '#f97316' : '#fff', color: selectedLocIds.length === locations.length ? '#fff' : '#f97316', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
                    >
                      Hamısı
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedLocIds([])}
                      style={{ padding: '5px 14px', borderRadius: 8, border: '1.5px solid #e5e7eb', background: '#fff', color: '#666', fontSize: 12, cursor: 'pointer' }}
                    >
                      Sıfırla
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {locations.map(loc => (
                      <label key={loc.id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${selectedLocIds.includes(loc.id) ? '#f97316' : '#e5e7eb'}`, background: selectedLocIds.includes(loc.id) ? '#fff7ed' : '#fafafa', fontSize: 13, fontWeight: selectedLocIds.includes(loc.id) ? 700 : 400 }}>
                        <input
                          type="checkbox"
                          checked={selectedLocIds.includes(loc.id)}
                          onChange={e => setSelectedLocIds(prev =>
                            e.target.checked ? [...prev, loc.id] : prev.filter(id => id !== loc.id)
                          )}
                          style={{ width: 16, height: 16, accentColor: '#f97316' }}
                        />
                        {loc.is_main ? '⭐ ' : '📍 '}{loc.name}
                        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#aaa' }}>{loc.city}</span>
                      </label>
                    ))}
                  </div>
                  {selectedLocIds.length === 0 && (
                    <p style={{ color: '#ef4444', fontSize: 12, marginTop: 6 }}>Ən az 1 filial seçin</p>
                  )}
                </div>
              )}

              {/* Basic Info */}
              <div className="form-group">
                <label>Başlıq *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="məs: San Sebastian 10% Endirim!"
                  required
                />
              </div>

              <div className="form-group">
                <label>Açıqlama</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Kampanya haqqında ətraflı məlumat..."
                  rows="3"
                />
              </div>

              {/* Kateqoriya */}
              <div className="form-group">
                <label>📱 App Kateqoriyası *</label>
                <p style={{ fontSize: 12, color: '#888', margin: '4px 0 8px' }}>
                  Seçdiyiniz kateqoriyada kampaniya Cafegoo appında görünəcək
                </p>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                >
                  <option value="general">🔥 Ümumi</option>
                  <option value="coffee">☕ Kofe</option>
                  <option value="lunch">🍱 Biznes Lunch</option>
                  <option value="breakfast">🥐 Səhər yeməyi</option>
                  <option value="dessert">🍰 Şirniyyat</option>
                  <option value="discount">💰 Endirimlər</option>
                  <option value="free">🎁 Pulsuz təklif</option>
                  <option value="combo">🍔 Kombo təklif</option>
                  <option value="weekend">🎉 Həftəsonu</option>
                  <option value="happy_hour">🕐 Happy Hour</option>
                </select>
              </div>

              {/* Campaign Type */}
              <div className="form-group">
                <label>Kampanya Növü *</label>
                <select
                  value={formData.campaign_type}
                  onChange={(e) => setFormData({ ...formData, campaign_type: e.target.value })}
                  required
                >
                  <option value="discount">💰 Endirim (%-lə)</option>
                  <option value="bogo">🎁 1 Alana 1 Hədiyyə</option>
                  <option value="free_item">🎁 Pulsuz Məhsul</option>
                  <option value="points_boost">⭐ Xal Bonus</option>
                  <option value="combo_deal">🍔 Kombo Təklif</option>
                </select>
              </div>

              {/* Conditional Fields */}
              {formData.campaign_type === 'discount' && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Endirim % *</label>
                      <input
                        type="number"
                        value={formData.discount_percentage}
                        onChange={(e) => setFormData({ ...formData, discount_percentage: e.target.value })}
                        placeholder="10"
                        min="1"
                        max="100"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Əvvəlki Qiymət (₼)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.original_price}
                        onChange={(e) => setFormData({ ...formData, original_price: e.target.value })}
                        placeholder="15.00"
                      />
                    </div>
                    <div className="form-group">
                      <label>Yeni Qiymət (₼)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.discounted_price}
                        onChange={(e) => setFormData({ ...formData, discounted_price: e.target.value })}
                        placeholder="13.50"
                      />
                    </div>
                  </div>
                </>
              )}

              {formData.campaign_type === 'points_boost' && (
                <div className="form-group">
                  <label>Tələb Olunan Xal *</label>
                  <input
                    type="number"
                    value={formData.points_required}
                    onChange={(e) => setFormData({ ...formData, points_required: e.target.value })}
                    placeholder="50"
                    required
                  />
                  <small>Normal 100 xal əvəzinə 50 xalla al</small>
                </div>
              )}

              {formData.campaign_type === 'free_item' && (
                <div className="form-group">
                  <label>Pulsuz Məhsul Adı *</label>
                  <input
                    type="text"
                    value={formData.free_item_name}
                    onChange={(e) => setFormData({ ...formData, free_item_name: e.target.value })}
                    placeholder="məs: Pulsuz Desert"
                    required
                  />
                </div>
              )}

              {formData.campaign_type === 'bogo' && (
                <div className="bogo-form-block">
                  <div className="bogo-form-header">
                    <span>🎁 1 Alana 1 Hədiyyə Şərtləri</span>
                  </div>
                  <div className="form-group">
                    <label>Hansı məhsulu alsın? *</label>
                    <input
                      type="text"
                      value={formData.buy_item_name}
                      onChange={(e) => setFormData({ ...formData, buy_item_name: e.target.value })}
                      placeholder="məs: Latte, San Sebastian, Cheesecake"
                      required
                    />
                    <small>Müştərinin satın almalı olduğu məhsul</small>
                  </div>
                  <div className="form-group">
                    <label>Nəyi qazansın? *</label>
                    <input
                      type="text"
                      value={formData.get_item_name}
                      onChange={(e) => setFormData({ ...formData, get_item_name: e.target.value })}
                      placeholder="məs: Desert, Espresso, Keks"
                      required
                    />
                    <small>Müştərinin hədiyyə/endirimli alacağı məhsul</small>
                  </div>
                  <div className="form-group">
                    <label>Hədiyyə növü *</label>
                    <select
                      value={formData.get_item_discount}
                      onChange={(e) => setFormData({ ...formData, get_item_discount: e.target.value })}
                    >
                      <option value="100">🎁 Tamamilə pulsuz (1+1)</option>
                      <option value="50">💰 50% endirim</option>
                      <option value="25">💰 25% endirim</option>
                      <option value="custom">✏️ Digər faiz...</option>
                    </select>
                  </div>
                  {formData.get_item_discount === 'custom' && (
                    <div className="form-group">
                      <label>Endirim faizi *</label>
                      <input
                        type="number"
                        min="1"
                        max="99"
                        placeholder="məs: 30"
                        onChange={(e) => setFormData({ ...formData, get_item_discount: e.target.value })}
                      />
                    </div>
                  )}
                  <div className="bogo-preview">
                    <span>👁 Önizləmə: </span>
                    <strong>{formData.buy_item_name || '?'}</strong>
                    <span> alana → </span>
                    <strong>{formData.get_item_name || '?'}</strong>
                    <span> </span>
                    {formData.get_item_discount === '100'
                      ? <span className="bogo-free-tag">Pulsuz</span>
                      : <span className="bogo-discount-tag">{formData.get_item_discount}% endirim</span>
                    }
                  </div>
                </div>
              )}

              {/* Banner şəkli */}
              <div className="form-group">
                <label>📸 Kampaniya Banneri <span style={{ color: '#999', fontWeight: 400 }}>(isteğe bağlı)</span></label>
                {formData.banner_url && (
                  <div style={{ position: 'relative', marginBottom: 10 }}>
                    <img
                      src={thumbUrl(formData.banner_url)}
                      alt="banner"
                      style={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: 8, border: '1px solid #ddd' }}
                    />
                    <button
                      type="button"
                      onClick={() => setFormData(p => ({ ...p, banner_url: '' }))}
                      style={{
                        position: 'absolute', top: 6, right: 6,
                        background: 'rgba(0,0,0,0.6)', color: '#fff',
                        border: 'none', borderRadius: '50%',
                        width: 28, height: 28, cursor: 'pointer', fontSize: 14,
                      }}
                    >✕</button>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleBannerUpload}
                  disabled={bannerUploading}
                  style={{ padding: 8 }}
                />
                {bannerUploading && <small style={{ color: '#667eea' }}>⏳ Yüklənir...</small>}
                <small>Yükləməsəniz, filialın öz şəkli istifadə olunacaq</small>
              </div>

              {/* Badge */}
              <div className="form-row">
                <div className="form-group">
                  <label>Badge Yazı</label>
                  <input
                    type="text"
                    value={formData.badge_text}
                    onChange={(e) => setFormData({ ...formData, badge_text: e.target.value })}
                    placeholder="HOT, -50%, YENİ"
                  />
                </div>
                <div className="form-group">
                  <label>Badge Rəngi</label>
                  <input
                    type="color"
                    value={formData.badge_color}
                    onChange={(e) => setFormData({ ...formData, badge_color: e.target.value })}
                  />
                </div>
              </div>

              {/* Duration */}
              <div className="form-group">
                <label>Kampanya Müddəti *</label>
                <select
                  value={formData.duration_type}
                  onChange={(e) => setFormData({ ...formData, duration_type: e.target.value })}
                  required
                >
                  <option value="daily">📅 Günün Kampaniyası (1 gün)</option>
                  <option value="weekly">📆 Həftənin Kampaniyası (7 gün)</option>
                  <option value="monthly">🗓️ Ayın Kampaniyası (30 gün)</option>
                  <option value="custom">⚙️ Xüsusi Müddət</option>
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Başlama Tarixi *</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    required
                  />
                </div>
                {formData.duration_type === 'custom' && (
                  <div className="form-group">
                    <label>Bitmə Tarixi *</label>
                    <input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      required
                    />
                  </div>
                )}
              </div>

              {/* Settings */}
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                  />
                  <span>⭐ Əsas səhifədə öndə göstər</span>
                </label>
              </div>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_corporate_only}
                    onChange={(e) => setFormData({ ...formData, is_corporate_only: e.target.checked })}
                  />
                  <span>🏢 Yalnız korporativ müştərilər üçün</span>
                </label>
              </div>

              <div className="form-group">
                <label>Prioritet (0-100)</label>
                <input
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  placeholder="0"
                  min="0"
                  max="100"
                />
                <small>Yüksək prioritet əvvəl göstərilir</small>
              </div>

              {/* Actions */}
              <div className="modal-actions">
                <button type="submit" className="submit-btn" disabled={loading}>
                  {loading ? 'Yadda saxlanılır...' : (editingCampaign ? '💾 Yenilə' : '✅ Yarat')}
                </button>
                <button
                  type="button"
                  className="cancel-modal-btn"
                  onClick={() => { setShowModal(false); resetForm(); }}
                >
                  ❌ Ləğv Et
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

function tabStyle(active) {
  return {
    padding: '7px 16px', borderRadius: 20, border: 'none',
    background: active ? '#f97316' : '#f3f4f6',
    color: active ? '#fff' : '#444',
    fontWeight: active ? 700 : 500,
    fontSize: 13, cursor: 'pointer',
  };
}

export default CampaignsPage;